// SS API documentation https://www.soundslice.com/help/en/embedding/javascript-api/38/introduction/
import {Subject} from "rxjs";
import { SoundsliceResponseEventTypes} from "./SoundsliceResponseEventTypes";
import Note from "../../../models/soundslice/Note";
import {ISoundSliceRequestContext} from "./SoundsliceRequestEventTypes";
import {logger} from "../../log/logger";

const messageFromSoundsliceIframeSubject = new Subject<SoundsliceResponseEventTypes>();

/**
 * Subscribe to this to receive messages from the Soundslice iframe.
 * Returns unsubscribe function
 * @param callback - function to be executed whenever an event is emitted via call to Subject next()
 */
export function subscribeToReceiveMessageFromSoundsliceIframeEvents(callback: (message: SoundsliceResponseEventTypes) => void){
  const protectedCallback = createProtectedCallback(callback);
  const subscription = messageFromSoundsliceIframeSubject.subscribe(protectedCallback);
  return subscription.unsubscribe.bind(subscription);
}

function createProtectedCallback(callback: (message: SoundsliceResponseEventTypes) => void){
  return (message: SoundsliceResponseEventTypes) => {
    try{
      callback(message);
    }catch(e){
      logger.vendorSoundSlice.error({message: `error encountered in observer of receiving soundslice iframe messages: `, error: e as Error});
    }
  };
}

function publishMessageReceivedFromSoundsliceIframe(message: SoundsliceResponseEventTypes){
  messageFromSoundsliceIframeSubject.next(message);
}

export function ssGetNotes(notes: Note[], state?: ISoundSliceRequestContext){
  publishMessageReceivedFromSoundsliceIframe({type: "ssGetNotes", notes, state});
}

export function ssNotationLoaded(state?: ISoundSliceRequestContext){
  publishMessageReceivedFromSoundsliceIframe({type: "ssNotationLoaded", state});
}

export function ssPlay(state?: ISoundSliceRequestContext){
  publishMessageReceivedFromSoundsliceIframe({type: "ssPlay", state});
}

export function ssAudioEnd(state?: ISoundSliceRequestContext){
  publishMessageReceivedFromSoundsliceIframe({type: "ssAudioEnd", state});
}

export function ssBarCount(barCount: number, state?: ISoundSliceRequestContext){
  publishMessageReceivedFromSoundsliceIframe({type: "ssBarCount", barCount, state});
}

export function ssDuration(durationSeconds: number, state?: ISoundSliceRequestContext){
  publishMessageReceivedFromSoundsliceIframe({type: "ssDuration", durationSeconds, state});
}

export function ssNoteClick(pitch: number, state?: ISoundSliceRequestContext){
  publishMessageReceivedFromSoundsliceIframe({type: "ssNoteClick", pitch, state});
}

export function ssMouseUp(state?: ISoundSliceRequestContext){
  publishMessageReceivedFromSoundsliceIframe({type: "ssMouseUp", state});
}

export function ssSeek(seekSeconds: number, state?: ISoundSliceRequestContext){
  publishMessageReceivedFromSoundsliceIframe({type: "ssSeek", offset: seekSeconds, state});
}

export function ssCurrentBar(barIndex: number, state?: ISoundSliceRequestContext){
  publishMessageReceivedFromSoundsliceIframe({type: "ssCurrentBar", barIndex, state});
}

export function ssAudioLoaded(recordingIndex: number, state?: ISoundSliceRequestContext){
  publishMessageReceivedFromSoundsliceIframe({type: "ssAudioLoaded", recordingIndex, state});
}

export function ssLoopChanged(loopStartOffsetSeconds: number, loopEndOffsetSeconds: number, state?: ISoundSliceRequestContext){
  publishMessageReceivedFromSoundsliceIframe({type: "ssLoopChange", loopStartOffsetSeconds, loopEndOffsetSeconds, state});
}

export function ssPause(state?: ISoundSliceRequestContext){
  publishMessageReceivedFromSoundsliceIframe({type: "ssPause", state});
}

export function ssCurrentTime(offset: number, state?: ISoundSliceRequestContext){
  publishMessageReceivedFromSoundsliceIframe({type: "ssCurrentTime", offset, state});
}

export function ssSpeed(speed: number, state?: ISoundSliceRequestContext){
  publishMessageReceivedFromSoundsliceIframe({type: "ssSpeed", speed, state});
}

export function ssAudioSourceChanged(recordingIndex: number, state?: ISoundSliceRequestContext){
  publishMessageReceivedFromSoundsliceIframe({type: "ssAudioSourceChanged", recordingIndex, state});
}

export function ssPlayerReady(state?: ISoundSliceRequestContext){
  publishMessageReceivedFromSoundsliceIframe({type: "ssPlayerReady", state});
}

export function ssOneTimeLoopEnd(state?: ISoundSliceRequestContext){
  publishMessageReceivedFromSoundsliceIframe({type: "ssOneTimeLoopEnd", state});
}

export function ssZoom(zoomLevel: number, state?: ISoundSliceRequestContext){
  publishMessageReceivedFromSoundsliceIframe({type: "ssZoom", zoomLevel, state});
}
